import * as React from 'react';

import { Box, CircularProgress } from '@mui/material';
import Avatar, { AvatarProps } from '@mui/material/Avatar';

import PlayIcon from '../../PlayIcon/PlayIcon';

export type LazyAvatarProps = {
  placeholderSrc?: string;
  isVideo?: boolean;
} & AvatarProps;

const LazyAvatar: React.FC<LazyAvatarProps> = React.forwardRef(
  ({ placeholderSrc, src, alt, isVideo, sx, ...restProps }, ref) => {
    const [isLoading, setIsLoading] = React.useState(true);
    const [hasError, setHasError] = React.useState(0);
    const [currentSrc, setCurrentSrc] = React.useState(src);

    const handleRetry = React.useCallback(() => {
      setIsLoading(true);
      setCurrentSrc(`${src}?retry=${Date.now()}`); // Bypass cache
    }, [src]);

    React.useEffect(() => {
      setCurrentSrc(src);
      setIsLoading(true);
      setHasError(0);
    }, [src]);

    React.useEffect(() => {
      if (hasError > 0) {
        handleRetry();
      }
    }, [handleRetry, hasError]);

    return (
      <>
        {isLoading && hasError === 0 && (
          <Avatar
            alt={alt}
            src={placeholderSrc ?? src}
            sx={{ transition: 'visibility 0ms ease 400ms', ...sx }}
            {...restProps}
          />
        )}

        {isLoading && hasError > 0 && (
          <Box
            sx={{
              backgroundColor: '#F7F7F7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              height: '100%',
              width: '100%',
            }}
          >
            <CircularProgress size={20} />
          </Box>
        )}

        <Avatar
          alt={alt}
          src={currentSrc}
          sx={{
            transition: 'opacity 400ms ease 0ms',
            display: isLoading ? 'none' : 'block',
            ...sx,
          }}
          imgProps={{
            onLoad: () => {
              setHasError(0);
              setIsLoading(false);
            },
            onError: () => {
              setIsLoading(false);
              setHasError((prev) => prev + 1);
            },
          }}
          ref={ref}
          {...restProps}
        />

        {isVideo && <PlayIcon />}
      </>
    );
  },
);

export default LazyAvatar;